import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import Modal from "react-modal";
import classes from "./affiliateInterstitial.module.css";
import { AFFILIATE_INTERSTITIAL_COUNTDOWN } from "../../constant/constant";
import { recordAffiliateClick } from "../../service/affiliateStorageService";
import {
  AFFILIATE_REL,
  trackAffiliateInterstitial,
} from "../../utils/trackAffiliate";
import useAffiliateInterstitial from "../../hooks/useAffiliateInterstitial";
import AffiliateDisclosure from "../AffiliateDisclosure";

Modal.setAppElement("#root");

/**
 * Quảng cáo dạng chen ngang, hiện sau khi người dùng đã hoàn thành một việc.
 *
 * Cố ý KHÔNG phải cổng chặn: không mã, không phần thưởng, không "mở khoá".
 * Đóng đi thì người dùng không mất gì cả. Đây là ranh giới tách nó khỏi kiểu ép
 * click mà chính sách chống gian lận của Shopee xử phạt — hiện quảng cáo thì
 * được, bắt người ta click mới cho dùng tiếp thì không.
 */
function AffiliateInterstitial() {
  const { product, trigger } = useAffiliateInterstitial();
  const [isOpen, setIsOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(
    AFFILIATE_INTERSTITIAL_COUNTDOWN
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!product) return;
    setIsOpen(true);
    setSecondsLeft(AFFILIATE_INTERSTITIAL_COUNTDOWN);
    trackAffiliateInterstitial("show", {
      productId: product.id,
      productName: product.name,
      trigger,
    });
  }, [product, trigger]);

  useEffect(() => {
    if (!isOpen) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen]);

  const closable = secondsLeft === 0;

  const handleClose = useCallback(() => {
    if (!closable) return;
    setIsOpen(false);
    if (product) {
      trackAffiliateInterstitial("close", {
        productId: product.id,
        productName: product.name,
        trigger,
      });
    }
  }, [closable, product, trigger]);

  const handleClick = useCallback(() => {
    if (!product) return;
    recordAffiliateClick(product.id);
    trackAffiliateInterstitial("click", {
      productId: product.id,
      productName: product.name,
      trigger,
    });
    setIsOpen(false);
  }, [product, trigger]);

  if (!product) return null;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={closable}
      shouldCloseOnEsc={closable}
      className={classes.modal}
      overlayClassName={classes.overlay}
    >
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.byline}>
            <span className={classes.avatar} aria-hidden="true">
              A
            </span>
            <span className={classes.bylineName}>Admin Grade PTIT gợi ý</span>
          </div>
          {closable ? (
            <button
              type="button"
              className={classes.close}
              onClick={handleClose}
              aria-label="Đóng"
            >
              ×
            </button>
          ) : (
            <span className={classes.countdown}>Đóng sau {secondsLeft}s</span>
          )}
        </div>

        <div className={classes.body}>
          {product.image && (
            <img
              src={product.image}
              alt={product.imageAlt ?? product.name}
              className={classes.image}
              width={110}
              height={110}
              loading="lazy"
            />
          )}

          <p className={classes.name}>{product.name}</p>
          {product.badge && (
            <span className={classes.badge}>{product.badge}</span>
          )}

          {product.noteAttribution && (
            <p className={classes.attribution}>{product.noteAttribution}</p>
          )}

          <ul className={classes.notes}>
            {product.adminNote.slice(0, 3).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>

          {product.priceRange && (
            <p className={classes.price}>{product.priceRange}</p>
          )}
        </div>

        <div className={classes.footer}>
          <a
            href={product.url}
            className={classes.cta}
            target="_blank"
            rel={AFFILIATE_REL}
            onClick={handleClick}
            onAuxClick={handleClick}
          >
            Xem trên Shopee →
          </a>
          <AffiliateDisclosure variant="chip" className={classes.disclosure} />
        </div>
      </div>
    </ReactModal>
  );
}

export default AffiliateInterstitial;
